import React, { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
	extractEventParams,
	envAwarePost,
	capitalize,
	resizeFile,
	dateToYMD,
} from '../../util/util'
import {
	toggleDeleteItem,
	deleteEventBatches,
	updateEditor,
	updateEvent,
	addItemToEvent,
	getEventDetails,
	createProgramEventCopy,
	applyAdditionalOptions,
	toggleWorkoutCompactMode
} from '../../state/events'
import useS3DirectUpload from '../../hooks/useS3DirectUpload'
import {update as updateResourceLibrary} from '../ResourceLibrary/slice'
import {
	selectAllClients,
	selectTrainerParam,
	selectClientsById,
	getAllClients
} from "../users/usersSlice"
import { useDebounceCallback } from '@react-hook/debounce'
import { useEvent } from '../../hooks/events'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Container from 'react-bootstrap/Container'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Spinner from 'react-bootstrap/Spinner'
import DatePicker from "react-datepicker"
import Routine from './Routine/Routine'
import Foods from './Foods/Foods'
import Select from "react-select"
import Library from '../ResourceLibrary/Library'
import PercentMaxCalculator from './PercentMaxCalculator/PercentMaxCalculator'
import useDirectUpload from '../../hooks/useDirectUpload' //DEPRECATED use useS3DirectUploadInstead
import useApiStatusDispatch from '../../hooks/useApiStatusDispatch'
import useApiStatusEnvAwarePost from '../../hooks/useApiStatusEnvAwarePost'
import classes from "./EventEditor.module.css"
import { updateEventSnapshot } from "../ProgramsAddEdit/ProgramsAddEditSlice"
import useUsersForEventCopy from '../../hooks/useUsersForEventCopy'
import { getLibraryExercises } from '../exerciseList/exerciseListSlice'
import TimePicker from '../TimePicker/TimePicker'
import {updateEventLinkEditor} from '../programs/programsSlice'
import {useMapClientsToOptions} from '../../hooks/useMapClientsToOptions';
import { 
	process24Hour,
	addZero,
	dateToTimeObj,
	stringToTimeObject,
	getTimeString 
} from '../../util/datetime'
import DeductSessionCheck from '../DeductSessionCheck/DeductSessionCheck'

/**
 * Resolves correct sub item list component for different event types
 * workouts have a routine of exercises
 * nutrition has a list of foods
 * tasks have no sub items
 */
const SubItems = ({ eventType, event }) => {
	if (!event) return null
	switch (eventType) {
		case "workout": return <Routine workout_id={event.workout_id} />
		case "nutrition": return <Foods nutrition_id={event.nutrition_id} />
		case "task": return null
	}
}

//Helper fucntion which maps event type to the "Selector" get param for legacy routing
const eventTypeToSelector = type => ['workout', 'task'].includes(type) ? `${capitalize(type)}s` : capitalize(type)

const ReadableRepeat = ({ repeatType, eventType, startDate, endDate, repeatData }) => {
	if (!repeatType || !eventType || !startDate || !endDate || !repeatData.length) return ''
	const repeatString = () => {
		if (repeatType === 'daily') {
			if (repeatData === "2x" || repeatData === "3x") {
				return `${repeatData} per day`
			}
			else if (parseInt(repeatData) === 1) {
				return 'every day'
			} else {
				return `every ${repeatData} days`
			}
		} else {
			return repeatData.join(', ')
		}
	}
	const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
	
	return (
		<small className="mt-2 mb-3 d-block text-center">
			This {eventType} will repeat starting after
			<span className="mx-1 text-primary">{startDate.toLocaleDateString(undefined, dateOptions)}</span>
			and repeat
			<span className="mx-1 text-primary">{repeatString()}</span>
			until
			<span className="mx-1 text-primary">{endDate.toLocaleDateString(undefined, dateOptions)}</span>
		</small>
	)
}

const NewFeatureAlert = ({message,id,variant="success",badge="NEW"})=>{
	const hiddenCount = localStorage.getItem(id)

	const [show,setShow] = useState(hiddenCount<3)

	const hide = ()=>{
		localStorage.setItem(id,hiddenCount+1)
		setShow(false)
	}
	
	if (!show) return null
	return (
		<Alert
			className="text-dark"
			variant={variant}
		>
			<div className="d-flex align-items-center">
				<Badge variant={variant} className="mr-1">{badge}</Badge>
				{message}
				<Button
					onClick={hide}
					variant="outline-secondary"
					className="ml-auto"
					size="sm"
				>
					<i className="fa fa-times"/>
				</Button>
			</div>
		</Alert>
	)
}

const EventEditor = ({ location }) => {
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();
	const statusEnvAwarePost = useApiStatusEnvAwarePost();

	const history = useHistory()
	//eventType = workout | task | nutrition
	//display = all | <event_id>
	//filter = client | favorite
	//event_id = null | <int> (if null then we are creating a new event)
	const {
		eventType,
		client_id,
		event_id,
		display,
		origin,
	} = extractEventParams(location.search)
	const trainerParam = useSelector(selectTrainerParam)
	const isClient = useSelector(state => state.users.current_user.account_type === "client")
	const selectedEventTypeList = eventType
	useEffect(() => {
		if (eventType === 'workout') dispatch(getLibraryExercises({ tgt_user_id_number: client_id }))
	}, [client_id, event_id, eventType])
	
	const event = useEvent(eventType, event_id)

	//favorite events have slightly different routing structure
	let eventListUrl;
	if (client_id === "favorite") {
		if (origin?.slice(0, 7) === "program") {
			dispatch(updateEventLinkEditor({
				show:true,
				eventMeta:{
					type:eventType,
					id:event_id
				}
			}))
			eventListUrl = `/program-editor/?id=${origin.slice(7)}`
		} else {
			eventListUrl = `/eventsView?display=all&selector=all&filter=${client_id}&${trainerParam}&view=list`
		}
	} else {
		if (origin === "myCalendar") {
			eventListUrl = `/scheduler`
		} else
			eventListUrl = isClient ? `/eventsView/?selector=all` :
				`/eventsView?client=${client_id}&display=all&filter=client&selector=all&${trainerParam}`
	}

	// update eventSnapshot if event is updated
	useEffect(()=>{
		if (origin?.slice(0, 7) === "program") {
			dispatch(updateEventSnapshot({ eventID: event_id, eventName: event?.title }))
		}
	},[event, origin])

	//if we are editing an existing event or creating a new
	const editing = Boolean(event_id)

	const {
		status: usersStatus,
		users,
		warning: usersWarning
	} = useUsersForEventCopy(eventType, event_id)
	
	const usersOptions = useMapClientsToOptions(users)

	const isRecurring = Boolean(event?.recurring)

	//const client = useSelector(state => state.users.clients.entities[client_id])
	const client = useSelector(state => selectClientsById(state, client_id))

	// make sure client is in state
	useEffect(() => {
		if (!client) {
			dispatch(getAllClients({include_compliance:0}))
		}
	}, [client])
	/* --- local state --- */

	const [title,setTitle] = useState(event?.title)

	//if undefined default to empty string
	const [linkProtocol, setLinkProtocol] = useState("");
	const [frequencyError, setFrequencyError] = useState(false);

	// none | daily | weekly
	const [repeatType, setRepeatType] = useState('none')
	const [addToSchedule, setAddtoSchedule] = useState(false)
	const [repeatUntil, setRepeatUntil] = useState(new Date())
	const [dailyDayString, setDailyDayString] = useState('1')
	const [customDayString, setCustomDayString] = useState("");
	const [additionalClients, setAdditionalClients] = useState([])
	const [startTime, setStartTime] = useState({
		hour: '',
		min: '',
		p: 'a'
	})
	const [endTime, setEndTime] = useState({
		hour: '',
		min: '',
		p: 'a'
	})
	const [applyToRecurring, setApplyToRecurring] = useState(0)
	const [applyType,setApplyType] = useState('single')
	const [repeatWeekDays, setRepeatWeekDays] = useState([])

	const {processUpload} = useS3DirectUpload()

	const [updateAll, setUpdateAll] = useState(false)

	const [publishingEvent, setPublishingEvent] = useState(false)
	const [draftingEvent, setDraftingEvent] = useState(false)
    const isTime24Hour = Number(useSelector(state => state.users.current_user.is_24_hour_time))

	/* --- effects --- */

	//since allEvents state may not always have the event we need
	//Get event data from the api
	useEffect(() => {

		dispatch(getEventDetails({
			event_type: eventType,
			event_id,
		})).then(res => {
			if (origin === "copyEdit") {
				console.log(dateToYMD(new Date()))
				updateEventGeneral('date', dateToYMD(new Date()))
			}
		})

		dispatch(updateEditor({id:event_id,type:eventType}))

		dispatch(updateResourceLibrary({
			tgtResourceId: event_id,
			tgtResourceType: eventType
		}))

	}, [event_id, eventType, origin])

	useEffect(() => {
		setAddtoSchedule(Boolean(event?.showOnSchedule))
		if (event?.start_time) setStartTime(stringToTimeObject(event.start_time, isTime24Hour))
		if (event?.end_time) setEndTime(stringToTimeObject(event.end_time, isTime24Hour))
		//if undefined default to empty string
		const trimmedLink = event?.link?.split('://')[1] || ''
		setLinkVal(trimmedLink)
		let protocol = event?.link?.split('://')[0]
		setLinkProtocol(protocol)
		setTitle(event?.title)
	}, [event])

	//helper function for adding/removing a day from the 
	//array of repeat weekdays
	const toggleWeeklyDayString = (day) => {
		if (repeatWeekDays.includes(day)) {
			setRepeatWeekDays(repeatWeekDays.filter(d => d !== day))
		} else {
			setRepeatWeekDays([...repeatWeekDays, day])
		}
	}

	// trim URL
	function trimUrl(url) {
		let urlArr = url?.split('://')
		if (urlArr?.length > 1) {
			if (urlArr[1] === "http" || urlArr[1] === "https") {
				url = urlArr[1] + "://" + urlArr[2]
			}
		}
		return url
	}

	const updateEventGeneral = (key, val) => {
		if (key === "link") {
			val = trimUrl(val)
		}
		return dispatch(updateEvent({
			event_type: eventType,
			[key]: val,
			event_id,
		})).then(res => console.log(res))
	}

	const clearDate = () => {
		dispatch(updateEvent({
			event_type: eventType,
			event_id,
			date:'',
			remove_date:1
		})).then(res => console.log(res))
	}



	const dispatchOptions = published => {
		if (published) {
			setPublishingEvent(true)
		} else {
			setDraftingEvent(true);
		}
		if (repeatType === 'daily') {
			if (dailyDayString === "custom") {
				if (customDayString < 1) {
					setFrequencyError(true)
					return
				}
			} else if (dailyDayString.length < 1) {
				setFrequencyError(true)
				return
			}
		} else if (repeatType === "weekly") {
			if (repeatWeekDays.length === 0) {
				setFrequencyError(true)
				return
			}
		}

		let payload = {
			additional_client_ids: additionalClients,
			apply_to_recurring: Number(['future','all'].includes(applyType)),
			future_only: Number(applyType==='future'),
			start_time: getTimeString(startTime, true),
			add_to_schedule: addToSchedule ? 1 : 0,
			end_time: getTimeString(endTime, true),
			end_date: dateToYMD(repeatUntil),
			recurrence_type: repeatType,
			src_event_id: event_id,
			event_type: eventType,
			date: client_id === 'favorite' ? "1969-12-31" : acceptableDate(event.date) ? event.date : null,
			published
		}

		if(published&&deductSession){
			payload.deduct_session = 1
		}
		if (repeatType === 'daily') {
			if (dailyDayString === "custom") {
				payload.day_string = customDayString
			} else {
				payload.day_string = dailyDayString
			}
		} else {
			payload.day_string = Array.from(repeatWeekDays).join(',')
		}

		statusDispatch(applyAdditionalOptions(payload), eventType, "update")
			.then( res => {
				setPublishingEvent(false)
				setDraftingEvent(false)
				history.push(eventListUrl)
			})
	}


	const handlePublish = () => {
		dispatchOptions(1)
	}

	const handleDraft = () => {
		dispatchOptions(0)
	}

	const handleDelete = () => {
		if (!window.confirm(`Are you sure you want to delete this ${eventType}?`)) return
		dispatch(toggleDeleteItem({
			itemType:eventType,
			id:event_id
		}))
		dispatch(deleteEventBatches({
			[eventType]:[event_id]
		}))
		history.push(eventListUrl)
	}

	const handleDeleteSeries = () => {
		if (!window.confirm(`Are you sure you want to delete this ${eventType} series?`)) return
		statusEnvAwarePost('api/events/api/', {
			action: 'removeEventSeries',
			event_type: eventType,
			recurring_id: event.recurring
		}, `${eventType} series`, "delete")
			.then(res => {
				console.log(res)
			})
		history.push(eventListUrl)
	}

	const [programCopyStatus,setProgramCopyStatus] = useState('idle')
	const handleCreateProgramEventCopy = (editNow=true)=>{
		if(!window.confirm('Are you sure you want to create a copy of this event that is not part of the program?')) return
		setProgramCopyStatus('pending')
		statusDispatch(
			createProgramEventCopy({event_type:eventType,event_id}),
			eventType,
			'create'
		).then(({payload})=>{
			setProgramCopyStatus('idle')
			if(payload.success === '1'&&editNow){
				const event_id = payload.data.event[`${eventType}_id`]
				const currentUrlParams = new URLSearchParams(window.location.search);
				currentUrlParams.set('event_id', event_id);
				history.replace({search:`?${currentUrlParams.toString()}`});
			}
		})
	}

	const handleUpdateTitle = e => {
		setTitle(e.target.value)
		debouncedUpdateEventGeneral('title', e.target.value)
	}

	const debouncedUpdateEventGeneral = useDebounceCallback(updateEventGeneral, 300)

	function acceptableDate(date) {
		return Boolean(date) && !(["1969-12-31", '0000-00-00'].includes(date))
	}

	/*--------------------------
	
	Logic/State for file/pdf
	
	--------------------------*/

	const [file,setFile] = useState(null)

	const [fileStatus,setFileStatus] = useState('idle')

	const handleUpdateFile = async () => {
		setFileStatus('pending')
		const s3Res = await processUpload(file)
		const url = s3Res.payload.url
		updateEventGeneral('file',url).then(()=>{
			setFileStatus('idle')
			setFile(null)
		})
	}

	const handleFileChange = e => {
		setFile(e.target.files[0])
	}

	const clearNewFile = setFile.bind(null,null)

	const handleRemoveFile = ()=>{
		updateEventGeneral('file','')
	}



	/*--------------------------
	
	Logic/State for preivew S3 upload flow
	
	--------------------------*/
	const [previewStatus, setPreviewStatus] = useState('idle')
	const [showFeatureModal, setShowFeatureModal] = useState(false)
	const [thumbnailUrl, setThumbnailUrl] = useState(false)
	const [featureMediaUrl, setFeatureMediaUrl] = useState(false)
	const [linkVal, setLinkVal] = useState('')
	//direct upload stuff for full size image
	const {
		file: featureMedia,
		setUpload: setFeatureMedia,
		clearUpload: clearFeatureMedia,
		handleUpload: handleFeatureDirectUpload,
	} = useDirectUpload({ //DEPRECATED use useS3DirectUploadInstead
		success: url => {
			setFeatureMediaUrl(url)
		},
		fail: () => {
			alert('failed to save preview')
		}
	})
	//direct upload stuff for thumbnail 
	const {
		file: thumbnail,
		setUpload: setThumbnail,
		clearUpload: clearThumbnail,
		handleUpload: handleThumbnailDirectUpload,
	} = useDirectUpload({
		success: url => {
			setThumbnailUrl(url)
		},
		fail: () => {
			alert('failed to generate thumbnail')
		}
	})

	const [deductSession,setDeductSession] = useState(false)

	//removes existing preview from event
	const handleRemovePreview = () => {
		dispatch(updateEvent({
			event_type: eventType,
			feature_media: '',
			thumbnail: '',
			event_id
		}))
	}

	//clears full sized image/gif and auto generated thumbnail
	const clearNewPreview = () => {
		clearThumbnail()
		clearFeatureMedia()
	}

	//when ever a new preview file is selcted this will 
	//set the preview media to the file contents and
	//auto generate a thumnail 
	const handlePreviewChange = async e => {
		const file = e.target.files[0]
		setFeatureMedia(file)
		//compress file to width 128 and set thumbnail
		const compressedFile = await resizeFile(file, 80, 80)
		console.log(compressedFile)
		setThumbnail(new File([compressedFile], 'thumbnail.png', { type: "image/png", }))
	}

	//if a user clicks save this will initiate the S3 upload flow for 
	//both the full sized feature image/gif and the generated thumbnail
	const handleUpdatePreview = () => {
		setPreviewStatus('pending')
		//initiate S3 upload flow for full sized feature
		handleFeatureDirectUpload()
		//initiate S3 upload flow for thumbnail
		handleThumbnailDirectUpload()
	}

	const handleToggleCompactMode = (e) => {
		dispatch(toggleWorkoutCompactMode(event_id))
	}

	//since we need to upload a feature & a thumb
	//and both of these saves happen async. we need
	//a helper function that will only save once we 
	//both
	useEffect(() => {
		if (!Boolean(featureMediaUrl) || !Boolean(thumbnailUrl)) return
		dispatch(updateEvent({
			feature_media: featureMediaUrl,
			thumbnail: thumbnailUrl,
			event_type: eventType,
			event_id
		})).then(() => {
			setPreviewStatus('idle')
			//clear state used for updating thumbnails/previews
			//WARNING: must clear the URLS or infinite render loop occurs
			setFeatureMediaUrl(false)
			setThumbnailUrl(false)
			clearNewPreview()
		})
	}, [featureMediaUrl, thumbnailUrl])


	if (!event) return (
		<div className="d-flex flex-column justify-content-center align-items-center" style={{
			height: '300px',
			width: '100%',
		}}>
			<Spinner animation="border" />
			<div className="mt-2">
				Setting up editor...
			</div>
		</div>
	)
	return (
		//WARNING
		//DO NOT wrap in a div
		//this content MUST be directly a child of the body content
		<>
			{eventType !== 'task' &&
				<Library />
			}
			<div>
				<Container>
					{!isClient && <Row>
						<Link to={`/trainer?${trainerParam}`}>Client List</Link>
						<span className="mx-1">/</span>
						<Link to={eventListUrl}>

							{(client_id === "favorite") && 'Favorites'}
							{(client_id !== "favorite") && typeof client?.user_name !== 'undefined' && `${client?.user_name}'s ${eventTypeToSelector(eventType)}`}

						</Link>
						<span className="mx-1">/</span>
						<span>{editing ? 'Edit' : 'Add'} {capitalize(eventType)}</span>
						<hr />
					</Row>}
				</Container>
				{(Boolean(event.part_of_program)&&client_id==='favorite')&&
					<Container>
						<Alert variant="info" className="text-center text-dark py-1 px-2">
							<small>
								<strong className="mr-1">
									NOTE:
								</strong> 
								This {eventType} is part of a program. Edits affect BOTH the program and this {eventType}.
							</small>
							<div className="d-flex flex-wrap">
								<OverlayTrigger overlay={
									<Tooltip>You will be redirected to the editor for the copy</Tooltip>
								}>
									<Button 
										disabled={programCopyStatus==='pending'}
										onClick={handleCreateProgramEventCopy}
										variant="outline-dark d-block" 
										className="mx-auto mt-2" 
										size="sm"
									>
										<span className="mr-1"><strong>Create a copy</strong> that is <strong>not part of the program</strong> and <strong>edit now</strong></span>
										{programCopyStatus==='pending'&&<Spinner animation="border" size="sm"/>}
									</Button>
								</OverlayTrigger>
							</div>
						</Alert>
					</Container>
				}
				{/* --- General Info --- */}
				<Container className="bg-light p-3 rounded mb-3" style={{ border: '1px solid #ddd' }}>
					<Row className="mb-1">
						<Col>
							<span style={{ fontSize: '18px', fontWeight: 'bold' }}>General</span>
							<hr />
						</Col>
					</Row>
					<Row className="mb-1">
						<Col sm="12" lg="2">
							<Form.Label>Name</Form.Label>
						</Col>
						<Col sm="12" lg="10">
							<Form.Control
								value={title}
								onChange={handleUpdateTitle}
								placeholder={`${capitalize(eventType)} Name`}
							/>
						</Col>
					</Row>
					{(client_id !== 'favorite') &&
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Date</Form.Label>
							</Col>
							<Col style={{ position: 'relative', zIndex: 10 }} sm="12" lg="10">
								<DatePicker
									className="form-control"
									onChange={newDate => {
										console.log(newDate)
										if(newDate === null){
											clearDate()
										}else{
											updateEventGeneral('date', newDate ? dateToYMD(newDate) : null)
										}
									}}
									isClearable
									placeholderText="Select Date"
									customInput={<Form.Control />}
									dateFormat="EEE, MMM d, yyyy"
									selected={
										//there are issues with dates showing 
										//incorrectly that do not have THH:MM:SS 
										//so add this to the date if it is set 
										//otherwise just instantate a new date for now
										(acceptableDate(event?.date)) ?
											new Date(event?.date.replace(/-/g, '\/'))
											: null}
								/>
							</Col>
						</Row>
					}
					<Row className="mb-1">
						<Col sm="12" lg="2">
							<Form.Label>Notes</Form.Label>
						</Col>
						<Col sm="12" lg="10">
							<Form.Control
								style={{
									height:'200px'
								}}
								defaultValue={event.notes}
								onChange={e => {
									debouncedUpdateEventGeneral('notes', e.target.value)
								}}
								as="textarea"
								id="eventNotesForm"
								placeholder={`${capitalize(eventType)} Notes`}
							/>

							{(document.getElementById("eventNotesForm")?.value)?.length !== 0 &&
								<span
									style={{
										backgroundColor: "transparent",
										border: 0,
										outline: 0,
										padding: "0px 6px 0px 0px",
										position: "absolute",
										top: 0,
										display: 'block',
										right: "15px",
										display: "table-cell",
										verticalAlign: "middle",
										cursor: "pointer"
									}}
									onClick={(e) => { (document.getElementById("eventNotesForm").value = ""); }}
								>&#x2612;</span>
							}
						</Col>
					</Row>
					<Row className="mb-1">
						<Col sm="12" lg="2">
							<Form.Label>Link</Form.Label>
						</Col>
						<Col sm="12" lg="10">
							<div className="input-group">
								<div className="input-group-prepend">
									<select
										value={linkProtocol}
										className="input-group-text"
										onChange={e => {
											const currentLinkProtocol = e.target.value;
											setLinkProtocol(currentLinkProtocol)
											debouncedUpdateEventGeneral(
												'link', `${currentLinkProtocol}://${linkVal}`
											)
										}}
									>
										<option value="https">https://</option>
										<option value="http">http://</option>
									</select>
								</div>
								<Form.Control
									onChange={e => {
										const currentLinkVal = (e.target.value)
										setLinkVal(currentLinkVal)
										//debouncedUrlTrim(val)
										debouncedUpdateEventGeneral(
											'link', `${linkProtocol}://${currentLinkVal}`
										)
									}}
									value={linkVal}
									placeholder="optional"
								/>
								{linkVal &&
									// <Button size="sm" variant="secondary" as={Link} to={`${linkProtocol}://${linkVal}`} >Test Link</Button>
									<a className="btn btn-secondary" target="_blank" rel="noreferrer noopener" style={{ color: 'white', borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }} href={`${linkProtocol}://${linkVal}`}>Test Link</a>
								}
							</div>
						</Col>
					</Row>
					<Row className="mb-1">
						<Col sm="12" lg="2">
							<Form.Label>
								Preview (optional)
								<OverlayTrigger overlay={
									<Tooltip>
										Image/gif to be displayed next to this {eventType}
									</Tooltip>
								}>
									<i className="fas fa-info-circle" />
								</OverlayTrigger>
							</Form.Label>
						</Col>
						<Col sm="12" lg="10">
							{/* <Form.File
								onChange={handlePreviewChange}
								label={`${capitalize(eventType)} Preview`}
								custom
							/> */}
							<label htmlFor="newProfilePhoto" className="btn btn-secondary btn-sm fileinput-button" style={{ marginTop: '7px' }}>
								Upload Preview Image or Gif
							</label>

							<input style={{ display: "none" }} type="file" id="newProfilePhoto" name="profilePicFile"
								onChange={handlePreviewChange}
							/>
							{Boolean(featureMedia) &&
								<div className="mt-2">
									<small>{featureMedia.name}</small>
									<Button
										disabled={previewStatus === 'pending'}
										variant="outline-danger"
										onClick={clearNewPreview}
										className="ml-2"
										size="sm"
									>
										clear
									</Button>
									<Button
										disabled={previewStatus === 'pending'}
										variant="outline-primary"
										onClick={handleUpdatePreview}
										className="ml-2"
										size="sm"
									>
										<span className="mr-1">Save</span>
										{previewStatus === 'pending' && <Spinner size="sm" animation="border" />}
									</Button>
								</div>
							}
							{(Boolean(event.thumbnail) || thumbnail) &&

								<div
									className="d-inline-flex flex-column align-items-center"
								>
									<div
										className="d-inline-flex flex-column align-items-center"
										onClick={() => { setShowFeatureModal(true) }}
										style={{ cursor: 'pointer' }}
										tabindex={0}
									>
										<small>Thumbnail Preview</small>
										<img
											style={{ width: '80px', display: 'block' }}
											src={
												thumbnail ?
													URL.createObjectURL(thumbnail) :
													event.thumbnail
											}
										/>
									</div>
									{(Boolean(event.thumbnail) || !thumbnail) &&
										<Button
											onClick={handleRemovePreview}
											className="text-danger"
											variant="link"
										>
											Remove
										</Button>
									}
								</div>
							}
						</Col>
					</Row>
					<Row className="mb-1">
						<Col sm="12" lg="2">
							<Form.Label>
								File/PDF (optional)
								<OverlayTrigger overlay={
									<Tooltip>
										File/PDF linked to this {eventType}
									</Tooltip>
								}>
									<i className="fas fa-info-circle" />
								</OverlayTrigger>
							</Form.Label>
						</Col>
						<Col sm="12" lg="10">
							<Form.File
								custom
								label="File/PDF"
								id="event-file-pdf-input"
								onChange={handleFileChange}
							/>
							{Boolean(file) &&
								<div className="mt-2">
									<small>{file.name}</small>
									<Button
										disabled={fileStatus === 'pending'}
										variant="outline-danger"
										onClick={clearNewFile}
										className="ml-2"
										size="sm"
									>
										clear
									</Button>
									<Button
										disabled={fileStatus === 'pending'}
										variant="outline-primary"
										onClick={handleUpdateFile}
										className="ml-2"
										size="sm"
									>
										<span className="mr-1">Save</span>
										{fileStatus === 'pending' && <Spinner size="sm" animation="border" />}
									</Button>
								</div>
							}
							{(Boolean(event.file) && !file) &&
								<div>
									<div
										className="d-flex align-items-center flex-wrap"
									>
											<a href={file ? file.name : event.file} target="_blank">
												View current File/PDF
											</a>
											<Button
												onClick={handleRemoveFile}
												className="text-danger ml-1"
												variant="link"
											>
												Remove
											</Button>
									</div>
								</div>
							}
						</Col>
					</Row>
					{/* --- Compact mode toggling --- */}
					{eventType === 'workout'&&
						<>
							<Row className="mb-1">
								<Col sm="12" lg="2">
									<Form.Label>
										Compact Mode
										<OverlayTrigger overlay={
											<Tooltip>
												Use a single row for viewing/editting exercise data rather than a row for each set.
											</Tooltip>
										}>
											<i className="fas fa-info-circle ml-1" />
										</OverlayTrigger>
									</Form.Label>
								</Col>
								<Col sm="12" lg="10">
									<Form.Switch 
										checked={Boolean(event.compact_mode)}
										onChange={handleToggleCompactMode}
										id="compact-mode-switch" 
										label="compact mode"
									/>
									<small className={[
										Boolean(event.compact_mode)?'text-success':'text-muted',
										'ml-1'
									].join(' ')}>
										{Boolean(event.compact_mode)?'enabled (note: compact workouts are not compatible with automatic guided workouts)':'disabled'}
									</small>
								</Col>
							</Row>
						</>
					}
				</Container>
				{/* --- Nutrition Macro Goals (only trainers are allowed to edit) --- */}
				{(eventType === 'nutrition') &&
					<Container className="bg-light p-3 rounded mb-3" style={{ border: '1px solid #ddd' }}>
						<Row className="mb-1">
							<Col>
								<span style={{ fontSize: '18px', fontWeight: 'bold' }}>Macro Goals</span>
								<div>
									Set macro goals
								</div>
								<hr />
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Calorie Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.calorie_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('calorie_goal', e.target.value)
									}}
									placeholder="calorie goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Protein Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.protein_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('protein_goal', e.target.value)
									}}
									placeholder="protein goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Carb Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.carb_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('carb_goal', e.target.value)
									}}
									placeholder="carb goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Fat Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.fat_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('fat_goal', e.target.value)
									}}
									placeholder="fat goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Water Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.water_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('water_goal', e.target.value)
									}}
									placeholder="water goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Sodium Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.sodium_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('sodium_goal', e.target.value)
									}}
									placeholder="sodium goal"
								/>
							</Col>
						</Row>
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Fiber Goal</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Control
									defaultValue={event.fiber_goal}
									onChange={e => {
										debouncedUpdateEventGeneral('fiber_goal', e.target.value)
									}}
									placeholder="fiber goal"
								/>
							</Col>
						</Row>
					</Container>
				}
				{eventType==='workout'&&
					<Container className="mb-3">
						<NewFeatureAlert
							message="Customize tracked items and assign/track RPE. These features are only available on the website & iOS app - Android coming soon. If something isn't right please contact support@fitsw.com."
							variant="info"
							id="editTrackables"
							badge="BETA"
						/>
					</Container>
				}
				{/* --- Routine/Foods Editor --- */}
				{eventType !== 'task' &&
					<Container className="bg-light p-3 rounded mb-3" style={{ border: '1px solid #ddd' }}>
						<Row>
							<Col>
								<SubItems eventType={eventType} event={event} />
							</Col>
						</Row>
					</Container>
				}
				{/* --- Additional Details --- */}
				<Container className="bg-light p-3 rounded mb-3" style={{ border: '1px solid #ddd' }}>
					{(client_id !== 'favorite') &&
						<Row className="mb-2">
							<Col>
								<div style={{ fontSize: '18px', fontWeight: "bold" }}>
									Additional
								</div>
								After finalizing your {capitalize(eventType)} above, you can use this section for additional options.
							</Col>
						</Row>
					}
					{(!isRecurring && client_id !== 'favorite') &&
						<>
							<Row className="mb-1">
								<Col sm="12" lg="2">
									<Form.Label>Repeat</Form.Label>
								</Col>

								{(acceptableDate(event?.date)) ?
									<Col sm="12" md="3" lg="3">
										<Select class="form-select"
											options={[
												{ value: "none", label: "None" },
												{ value: "daily", label: "Daily" },
												{ value: "weekly", label: "Weekly" }
											]}
											defaultValue={{ value: "none", label: "None" }}
											onChange={e => {
												setRepeatType(e.value)
											}}
										/>
									</Col> :
									<div className="d-flex ml-4 my-2">
										<i className="fas fa-info-circle"> <span style={{ fontWeight: "normal" }}> You must provide a starting date to repeat </span></i>
									</div>
								}
							</Row>
							{repeatType !== 'none' &&
								<>
									<Row className="mb-1 mt-2" >
										<Col sm="12" lg="2">
											<Form.Label >Repeat Until</Form.Label>
										</Col>
										<Col sm="12" lg="10" >
											<DatePicker
												className="form-control"
												dateFormat="MMM dd, yyyy"
												onChange={newDate => {
													setRepeatUntil(newDate)
													setFrequencyError(false)
												}}
												selected={repeatUntil}

											/>
										</Col>
									</Row>

									{repeatType === 'daily' &&
										<Row className="mb-1 mt-2">
											<Col sm="12" lg="2">
												<Form.Label >Repeat Frequency </Form.Label>
											</Col>
											<Col sm="12" md="3" lg="3">
												<Form.Control as={"select"}
													value={dailyDayString}
													onChange={e => {
														setDailyDayString(e.target.value)
														setCustomDayString("")
													}
													}
													placeholder="days"
												>
													<option value="3x"> Three Times Per Day </option>
													<option value="2x"> Two Times Per Day </option>
													<option value="1"> Every Day </option>
													<option value="2"> Every 2nd Day </option>
													<option value="3"> Every 3rd Day </option>
													<option value="4"> Every 4th Day </option>
													<option value="5"> Every 5th Day </option>
													<option value="6"> Every 6th Day </option>
													<option value="7"> Every 7th Day </option>
													<option value="8"> Every 8th Day </option>
													<option value="9"> Every 9th Day </option>
													<option value="10"> Every 10th Day </option>
													<option value="custom"> Custom </option>
												</Form.Control>
											</Col>
										</Row>}

									{dailyDayString === "custom" &&
										<Row className="mb-1 mt-2">
											<Col sm="12" lg="2">
												<Form.Label >Custom Repeat Frequency </Form.Label>
											</Col>
											<Col sm="12" md="3" lg="3">
												<Form.Control
													type="number"
													onChange={e => {
														setCustomDayString(e.target.value)
														setFrequencyError(false)
													}}
													placeholder="days"
												/>
											</Col>

										</Row>}

									{repeatType === 'weekly' &&
										<Row className="mb-1">
											<Col sm="12" lg="2">
												<Form.Label>Choose Days</Form.Label>
											</Col>
											<Col sm="12" lg="10">
												<div className="d-flex justify-content-around">
													{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
														<Form.Check
															checked={repeatWeekDays.includes(d)}
															onChange={() => {
																setFrequencyError(false)
																toggleWeeklyDayString(d)
															}}
															id={`weekly-${d}`}
															label={d}
														/>
													))}
												</div>
												{frequencyError &&
													<div className="text-danger ml-3 mb-2">
														select at least one day
													</div>
												}
											</Col>

										</Row>
									}

									<ReadableRepeat
										repeatType={repeatType}
										eventType={eventType}
										startDate={new Date(event.date?.split(' ').join('T'))}
										endDate={repeatUntil}
										repeatData={
											repeatType === 'daily' ?
												customDayString ? customDayString : dailyDayString :
												repeatWeekDays
										}
									/>
								</>
							}
						</>
					}
					{(client_id !== 'favorite' && !isClient) &&
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Schedule</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								{acceptableDate(event?.date) ?
									<div className="d-flex">
										<Form.Check
											checked={addToSchedule}
											onChange={e => {
												setAddtoSchedule(e.target.checked)
											}}
										/>
										<div>
											Add to <a target="_blank" href="/scheduler/">My Calendar</a>?
										</div>
									</div>
									:
									<div className="d-flex my-2 ml-2">
										<i className="fas fa-info-circle"> <span style={{ fontWeight: "normal" }}> You must provide a starting date to add to your calendar </span></i>
									</div>}
							</Col>
						</Row>
					}
					{addToSchedule &&
						<Row className="mb-1">
							<Col>
								<div className="d-md-flex align-items-center">
									<TimePicker
										onChange={setStartTime}
										minuteInterval={15}
										hour={startTime.hour}
										minute={startTime.min}
										period={startTime.p}
										isTime24Hour={isTime24Hour}
									/>
									<span>-</span>
									<TimePicker
										onChange={setEndTime}
										minuteInterval={15}
										hour={endTime.hour}
										minute={endTime.min}
										period={endTime.p}
										isTime24Hour={isTime24Hour}
									/>
								</div>
							</Col>
						</Row>
					}
					{(!addToSchedule && !isRecurring && (client_id !== "favorite" && !isClient)) &&
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Additional Clients</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Select
									onChange={selectedOptions => {
										setAdditionalClients(selectedOptions.map(
											opt => opt.value
										))
									}}
									noOptionsMessage={() => (
										usersStatus === 'pending' ?
											'Loading clients...' :
											'No clients'
									)}
									options={usersOptions}
									isMulti
								/>
							</Col>
							{Boolean(usersWarning) &&
								<Col>
									<small className="text-muted">{usersWarning}</small>
								</Col>
							}
						</Row>
					}
					{isRecurring &&
						<Row className="mb-1">
							<Col sm="12" lg="2">
								<Form.Label>Repeat</Form.Label>
							</Col>
							<Col sm="12" lg="10">
								<Form.Check
									checked={applyType==='single'}
									label={`Only Update this ${capitalize(eventType)}`}
									onChange={e => {
										if (e.target.checked) {
											setApplyToRecurring(0)
											setApplyType('single')
										}
									}}
									id="update-current-only"
									name="update-options"
									type="radio"
								/>
								<Form.Check
									checked={applyType==='future'}
									label={`Update FUTURE ${capitalize(eventType)}s Linked to this One`}
									onChange={e => {
										if (e.target.checked) {
											setApplyType('future')
											setApplyToRecurring(1)
										}
									}}
									name="update-options"
									id="update-future"
									type="radio"
								/>
								<Form.Check
									checked={applyType==='all'}
									label={`Update ALL (including past) ${capitalize(eventType)}s Linked to this One`}
									onChange={e => {
										if (e.target.checked) {
											setApplyType('all')
											setApplyToRecurring(1)
										}
									}}
									name="update-options"
									id="update-all"
									type="radio"
								/>
							</Col>
						</Row>
					}
					<Row className="mt-3">
						<Col>
							<div className="d-flex justify-content-end flex-wrap">
								{Boolean(event.recurring)&&
									<OverlayTrigger overlay={
										<Tooltip>This {eventType} is part of a recurring series. Delete this {eventType} and all those part of the series.</Tooltip>
									}>
										<Button
											onClick={handleDeleteSeries}
											variant="outline-danger"
											className="ml-1 my-1"
										>
											Delete Event Series
										</Button>
									</OverlayTrigger>
								}
								<Button
									className="ml-1 my-1"
									onClick={handleDelete}
									variant="danger"
								>
									Delete 
								</Button>
								{!isClient && <Button
									disabled={draftingEvent}
									variant="primary"
									onClick={handleDraft}
									className="ml-1 my-1"
								>
									{draftingEvent ? <Spinner size="sm" animation="border" /> : "Save As Draft"}
								</Button>}
								<Button onClick={handlePublish} className="ml-1 my-1" variant="success" disabled={publishingEvent}>
									{publishingEvent ? <Spinner size="sm" animation="border" /> : `Publish ${capitalize(eventType)}`}
								</Button>
							</div>
							{client_id!=='favorite'&&
								<DeductSessionCheck
									label="Deduct session credit after publishing (optional)"
									onChange={e=>{
										setDeductSession(e.target.checked)
									}}
									checked={deductSession}
									client_id={client_id}
								/>
							}
						</Col>
					</Row>
					{client_id !== 'favorite' && !isClient &&
						<Row className="mt-3">
							<Col>
								<small className="text-muted">
									Publishing a {capitalize(eventType)} will display it in this specific client's {capitalize(eventType)} list.
									If you keep it in Draft form, your client will not see it.
								</small>
							</Col>
						</Row>
					}
				</Container>
				{eventType === 'workout' &&
					<Container className="bg-light p-3 rounded" style={{ border: '1px solid #ddd' }}>
						<Row className="mb-2">
							<Col>
								<div style={{ fontSize: '18px', fontWeight: "bold" }}>
									Tips & Tricks
								</div>
								<div id="playable-workouts">
									<p><em>How to enable guided/playable workouts</em></p>
									<ol>
										<li>Build your workout</li>
										<li>Add rest/time values in standard format (see below). Valid values have <i className="fa fa-play"/> above them</li>
										<li>Now all your client has to do is <em>press play</em> and FitSW will guide them through the workout using your specified times</li>
									</ol>
									<p>Standard time format - <em>MM:SS</em></p>
									<p>Examples:</p>
									<ul>
										<li>30 = 30 seconds</li>
										<li>5:00 = 5 minutes</li>
										<li>2:30 = 2 minutes & 30 seconds</li>
									</ul>
									<p>Hint: Input a 0 (zero) for no rest/time rather than leaving the input blank</p>
								</div>
							</Col>
						</Row>
					</Container>
				}
				<div style={{marginBottom:'300px'}}/>
			</div>
			{eventType === 'workout' &&
				<PercentMaxCalculator />
			}
			{Boolean(event.feature_media) &&
				<Modal
					onHide={() => { setShowFeatureModal(false) }}
					show={showFeatureModal}
					centered
				>
					<Modal.Header closeButton>
						Preview
					</Modal.Header>
					<Modal.Body>
						<img
							style={{ width: '100%', display: 'block' }}
							src={
								featureMedia ?
									URL.createObjectURL(featureMedia) :
									event.feature_media
							}
						/>
					</Modal.Body>
				</Modal>
			}
		</>
	)
}

export default EventEditor